import React, { useState, useRef, useMemo, useEffect } from 'react';
import { D, st, toArr, fmt, fmtDate, formatPhone, calcTotal, calcSubtotal, getToken, apiGet, apiPost, apiPut, apiDel, UNITS, capitalizeWords, DEFAULT_MAT_CATALOG, MAT_ALIASES, calcTax, taxLabel, getTaxRate, DEFAULT_PAYMENT_TERMS, DEFAULT_PAYMENT_TERMS_ENFORCED, defaultPaymentTerms, DEFAULT_SCOPE_NOTE } from '../constants.js';

// Company address is stored as ONE free-text field, but a letterhead wants the
// street on its own line above "City, ST ZIP". A newline in the stored value is
// honoured as-is. Otherwise we split after the street-type suffix rather than on
// the comma -- "21297 Braxfield Loop Estero, Fl 33928" has no comma after "Loop",
// and splitting on the comma would wrongly yield "...Loop Estero" / "Fl 33928".
// Suffix matching also survives multi-word cities (Bonita Springs, Fort Myers).
// If nothing matches we return the line untouched -- never mangle an address.
const ST_SUFFIX = /^(.*?\b(?:St|Street|Ave|Avenue|Rd|Road|Dr|Drive|Ln|Lane|Ct|Court|Loop|Way|Blvd|Boulevard|Pkwy|Parkway|Ter|Terrace|Cir|Circle|Pl|Place|Trl|Trail|Hwy|Highway|Sq|Square|Run|Bend|Point|Pt|Walk|Row|Path)\.?)\s+(.+)$/i;
export const addrLines = a => {
  const v = String(a || "").trim();
  if (!v) return [];
  const norm = l => l.replace(/,?\s*\b([A-Za-z]{2})\.?\s+(\d{5}(?:-\d{4})?)\s*$/, (_m, st, zip) => ", " + st.toUpperCase() + " " + zip);
  if (v.includes("\n")) return v.split("\n").map(x => x.trim()).filter(Boolean).map(norm);
  const m = v.match(ST_SUFFIX);
  return m ? [m[1].trim(), norm(m[2].trim())] : [norm(v)];
};

// Plain-text company block for emails: name, street, "City, ST ZIP", licence,
// phone -- one per line, empties dropped so a missing licence leaves no gap.
export const companySig = ci => [
  ci.name,
  ...addrLines(ci.address),
  ci.licenseNum && ("Lic# " + ci.licenseNum),
  ci.phone && formatPhone(ci.phone),
].filter(Boolean).join("\n");
import { Overlay, MHdr, Field } from './shared.jsx';

// Local helpers
const safeItems = v => Array.isArray(v) ? v.map(i=>({...i})) : [];
const emptyItem = () => ({desc:'',qty:1,unit:'hr',rate:0});
const EST_MARKUP = 20;

// These come from window globals set in App.jsx
const getMAT_CATALOG = () => window.MAT_CATALOG || {};
const getMAT_CATALOG_FLAT = () => window.MAT_CATALOG_FLAT || [];

const DOC_TEMPLATES = {
  standard: { id:'standard', label:'Standard', desc:'Clean professional layout with itemized line items' },
  detailed:  { id:'detailed', label:'Detailed',  desc:'Full labor & materials breakdown, good for commercial' },
  simple:    { id:'simple',   label:'Simple',    desc:'Scope + lump sum price, good for small residential jobs' },
  proposal:  { id:'proposal', label:'Proposal',  desc:'Formal proposal with description, scope, and signature' },
};

function NewClientInline({clients, value, onChange, onNewClient}) {
  const [mode, setMode] = React.useState("select");
  const [name, setName] = React.useState("");
  if (mode === "new") return (
    <div style={{display:"flex",gap:6}}>
      <input style={{...st.input,flex:1}} autoFocus placeholder="New client name..." value={name} onChange={e=>setName(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter"&&name.trim()){onNewClient(name.trim());setMode("select");setName("");}}}/>
      <button style={{...st.btn("primary"),marginLeft:0,whiteSpace:"nowrap"}} onClick={()=>{if(name.trim()){onNewClient(name.trim());setMode("select");setName("");}}}>+ Add</button>
      <button style={{...st.btn(),marginLeft:0}} onClick={()=>setMode("select")}>Cancel</button>
    </div>
  );
  return (
    <div style={{display:"flex",gap:6}}>
      <select style={{...st.input,flex:1}} value={value||""} onChange={e=>onChange(parseInt(e.target.value))}>
        <option value="">— Select client —</option>
        {toArr(clients).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <button style={{...st.btn(),marginLeft:0,whiteSpace:"nowrap",background:"#1e3a5f",border:"1px solid #2d5f9e",color:"#7eb8f7"}} onClick={()=>setMode("new")}>+ New</button>
    </div>
  );
}

export function SendDocModal({doc,type,clientEmail,clientPhone,onClose,onMarkSent,companyInfo}){
  const ci=companyInfo||{name:"AmpBooks",licenseNum:"",phone:"",address:"",paymentMethods:{check:true,cash:true}};
  const pm=ci.paymentMethods||{};
  const pmLines=[
    pm.check&&`   • Check payable to ${ci.name}`,
    pm.cash&&`   • Cash`,
    pm.venmo&&`   • Venmo: ${pm.venmoHandle||"@handle"}`,
    pm.zelle&&`   • Zelle: ${pm.zelleContact||"contact"}`,
    pm.paypal&&`   • PayPal: ${pm.paypalUser||"user"}`,
    pm.cashapp&&`   • Cash App: ${pm.cashappTag||"$tag"}`,
    pm.other&&`   • ${pm.otherDesc||"Other"}`,
  ].filter(Boolean).join("\n");
  const paymentSection=pmLines?`\n💳 Payment Options:\n${pmLines}\n`:`\n💳 To pay: contact us at ${ci.phone?formatPhone(ci.phone):""}\n`;

  const [phone,setPhone]=useState(clientPhone||"");
  const [to,setTo]=useState(clientEmail||"");
  const [subj,setSubj]=useState(`${type} ${doc.number} from ${ci.name}`);
  const [sent,setSent]=useState(false);
  const [sending,setSending]=useState(false);
  const [sendError,setSendError]=useState('');
  const [copied,setCopied]=useState(false);
  const [template,setTemplate]=useState(()=>{
    try{return localStorage.getItem('eb:docTemplate')||'standard';}catch(_){return'standard';}
  });
  const [showPreview,setShowPreview]=useState(false);

  const sub=calcSubtotal(doc.items),tax=calcTax(sub),total=sub+tax;
  const isInv=type==="Invoice";
  const depositLine=(()=>{
    if(!isInv&&doc.depositRequired&&doc.depositAmount){
      const amt=doc.depositType==="percent"?total*(parseFloat(doc.depositAmount)||0)/100:parseFloat(doc.depositAmount)||0;
      return`\n💰 Deposit Required: ${fmt(amt)} due upon acceptance${doc.depositType==="percent"?` (${doc.depositAmount}% of total)`:""}\n`;
    }
    return"";
  })();

  const [payNowUrl,setPayNowUrl]=useState("");
  React.useEffect(()=>{
    if(isInv&&doc.status!=="Paid"){
      apiGet('/connect/pay-link/'+doc.id).then(r=>setPayNowUrl(r.url||"")).catch(()=>setPayNowUrl(""));
    }
  },[doc.id,isInv,doc.status]);
  const payNowLine=isInv&&doc.status!=="Paid"&&payNowUrl?`\n🔗 Pay Online: ${payNowUrl}\n`:"";

  const smsMsg = (() => {
    const lines = [
      `Hi! Your ${type} #${doc.number} from ${ci.name} is ready.`,
      `Total: ${fmt(total)}`,
    ];
    if (!isInv && doc.depositRequired && doc.depositAmount) {
      const amt = doc.depositType === "percent" ? total*(parseFloat(doc.depositAmount)||0)/100 : parseFloat(doc.depositAmount)||0;
      lines.push(`Deposit required: ${fmt(amt)}`);
    }
    if (isInv && payNowUrl) lines.push(`Pay online: ${payNowUrl}`);
    if (ci.phone) lines.push(`Questions? ${formatPhone(ci.phone)}`);
    return lines.join('\n');
  })();
  const sendSms = () => { if (!phone.trim()) return; window.location.href = `sms:${phone}?body=${encodeURIComponent(smsMsg)}`; };
  const itemsText=(()=>{
    const g0=it=>Number(it.group)||0;
    const its=toArr(doc.items);
    const line=i=>`  ${i.desc} — ${i.qty} ${i.unit} x ${Number(i.rate).toFixed(2)} = ${(i.qty*i.rate).toFixed(2)}`;
    if(!its.some(it=>it.type==='job'&&String(it.desc||'').trim())) return its.filter(it=>it.type!=='job').map(line).join("\n");
    const grouped = [...new Set(its.filter(it=>it.type!=='permit').map(g0))].sort((a,b)=>a-b).map(g=>{
      const job=its.find(it=>it.type==='job'&&g0(it)===g);
      const rest=its.filter(it=>it.type!=='job'&&it.type!=='permit'&&g0(it)===g);
      const title=String(job&&job.desc||'').trim();
      // Flat jobs KEEP their material and labor lines now - they are the record
      // of what the job used - and those lines carry rate 0, so emailing them
      // would be a column of 0.00 rows telling the customer nothing. The filter
      // is on the AMOUNT, not on the mode, which is what keeps the emailed items
      // adding up to the total: anything still billing money still prints.
      if(job&&job.mode==='flat') return [title?`${title} — ${(Number(job.rate)||0).toFixed(2)}`:'',...rest.filter(r=>(Number(r.qty)||0)*(Number(r.rate)||0)!==0).map(line)].filter(Boolean).join("\n");
      return [title,...rest.map(line)].filter(Boolean).join("\n");
    }).filter(Boolean).join("\n\n");
    const permits = its.filter(it=>it.type==='permit').map(line).join("\n");
    return [grouped,permits].filter(Boolean).join("\n\n");
  })();
  const body=`To: ${to}\nSubject: ${subj}\n\nHello,\n\nPlease find your ${type.toLowerCase()} details below:\n\n${type} #: ${doc.number}\nDate: ${doc.date}${isInv&&doc.dueDate?`\nDue Date: ${doc.dueDate}`:""}\n\nLine Items:\n${itemsText}\n\nSubtotal: ${fmt(sub)}${tax>0?`\n${taxLabel()}: ${fmt(tax)}`:""}\n${isInv?"Total Due":"Estimate Total"}: ${fmt(total)}${depositLine}\n${isInv?paymentSection:""}${payNowLine}\nPayment Terms: ${ci.paymentTerms || defaultPaymentTerms(ci)}\n\nScope Note: ${ci.scopeNote || DEFAULT_SCOPE_NOTE}\n\nThank you for your business!\n\n${companySig(ci)}`;

  const copy=()=>{try{const ta=document.createElement("textarea");ta.value=body;ta.style.position="fixed";ta.style.opacity="0";document.body.appendChild(ta);ta.focus();ta.select();document.execCommand("copy");document.body.removeChild(ta);setCopied(true);setTimeout(()=>setCopied(false),2000);}catch(_){}};
  const send=async()=>{
    try{localStorage.setItem('eb:docTemplate',template);}catch(_){}
    setSending(true);setSendError('');
    try{
      const endpoint=isInv?`/email/invoice/${doc.id}`:`/email/estimate/${doc.id}`;
      await apiPost(endpoint,{});
    }catch(err){
      setSendError('Failed to send: '+(err.message||'Unknown error'));
      setSending(false);
      return;
    }
    setSending(false);
    setSent(true);
    try{onMarkSent();}catch(_){}
  };

  return(
    <Overlay zIndex={200}><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(600px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
      <MHdr title={`Send ${type} ${doc.number}`} onClose={onClose}/>

      {/* Template picker */}
      <div style={{marginBottom:16}}>
        <div style={{fontSize:11,color:D.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>Document Template</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {Object.values(DOC_TEMPLATES).map(t=>(
            <button key={t.id} onClick={()=>setTemplate(t.id)}
              style={{padding:"10px 12px",borderRadius:8,textAlign:"left",cursor:"pointer",fontFamily:"inherit",
                border:`1.5px solid ${template===t.id?D.accent:D.border}`,
                background:template===t.id?D.accent+"22":D.surface,
                transition:"all 0.15s"}}>
              <div style={{fontSize:13,fontWeight:600,color:template===t.id?D.accent:D.text,marginBottom:2}}>{t.label}</div>
              <div style={{fontSize:11,color:D.muted}}>{t.desc}</div>
            </button>
          ))}
        </div>
        <button style={{...st.btn(),marginLeft:0,marginTop:8,fontSize:11}} onClick={()=>setShowPreview(x=>!x)}>
          {showPreview?"Hide Preview":"👁 Preview Template"}
        </button>
        {showPreview&&<div style={{marginTop:12,maxHeight:400,overflowY:"auto",border:"1px solid "+D.border,borderRadius:8}}>
          <DocView doc={doc} clientName={ci.name} fmtDate={d=>d||""} type={type} extra={{dueDate:doc.dueDate,paidDate:doc.paidDate}} companyInfo={companyInfo} template={template}/>
        </div>}
      </div>

      <Field label="From"><input style={{...st.input,color:D.muted}} value="enjoisk8brdco@gmail.com" readOnly/></Field>
      <Field label="To"><input style={st.input} value={to} onChange={e=>setTo(e.target.value)} placeholder="client@example.com"/></Field>
      <Field label="Subject"><input style={st.input} value={subj} onChange={e=>setSubj(e.target.value)}/></Field>
      <Field label="Message preview"><textarea readOnly value={body} style={{...st.input,height:180,resize:"vertical",fontFamily:"monospace",fontSize:11,lineHeight:1.5}}/></Field>
      <div style={{background:D.surface,borderRadius:8,padding:"10px 12px",marginBottom:10,border:`1px solid ${D.border}`}}>
        <div style={{fontSize:11,color:D.muted,fontWeight:600,marginBottom:6}}>📱 Text to client</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input style={{...st.input,flex:1}} type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Client phone number"/>
          <button style={{...st.btn(),whiteSpace:"nowrap",background:"#0f2e1a",border:"1px solid "+D.success,color:D.success}} onClick={sendSms} disabled={!phone.trim()}>📱 Text</button>
        </div>
      </div>
      {sent&&<div style={{background:"#1a3a2a",border:"1px solid "+D.success,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13,color:D.success}}>✓ Email client opened. {type} marked as Sent.</div>}
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:8,flexWrap:"wrap"}}>
        <button style={st.btn()} onClick={onClose}>Close</button>
        <button style={{...st.btn(),background:copied?D.success:D.border,color:copied?"#fff":D.text}} onClick={copy}>{copied?"✓ Copied!":"Copy message"}</button>
        {sendError&&<div style={{color:D.danger,fontSize:12,marginBottom:8}}>{sendError}</div>}
        <button style={st.btn("primary")} onClick={send} disabled={sending}>{sending?"Sending...":"Send Email"}</button>
      </div>
    </div></Overlay>
  );
}

export function DocView({doc,clientName,fmtDate,type,extra,companyInfo,template}){
  const ci=companyInfo||{name:"AmpBooks",licenseNum:"",phone:"",address:"",ownerName:"",paymentMethods:{check:true,cash:true}};
  const pm=ci.paymentMethods||{};
  const pmList=[
    pm.check&&`Check (payable to ${ci.name})`,
    pm.cash&&"Cash",
    pm.venmo&&`Venmo: ${pm.venmoHandle||""}`,
    pm.zelle&&`Zelle: ${pm.zelleContact||""}`,
    pm.paypal&&`PayPal: ${pm.paypalUser||""}`,
    pm.cashapp&&`Cash App: ${pm.cashappTag||""}`,
    pm.other&&(pm.otherDesc||"Other"),
  ].filter(Boolean);
  const items=toArr(doc.items);
  const sub=calcSubtotal(items),tax=calcTax(sub),total=sub+tax;
  // Invoices are a list of jobs. A flat job prints its price on its own
  // heading row; a time & material job heads its materials and labor.
  const gOf=it=>Number(it.group)||0;
  const hasJobs=items.some(it=>it.type==='job'&&String(it.desc||'').trim());
  const row=(item,key)=>(<tr key={key}><td style={st.td}>{item.desc}</td><td style={st.td}>{item.qty}</td><td style={st.td}>{item.unit}</td><td style={{...st.td,textAlign:"right"}}>${Number(item.rate).toFixed(2)}</td><td style={{...st.td,textAlign:"right"}}>${(item.qty*item.rate).toFixed(2)}</td></tr>);
  const jobRows=()=>{
    if(!hasJobs) return items.filter(it=>it.type!=='job').map((item,i)=>row(item,i));
    const out=[];
    [...new Set(items.filter(it=>it.type!=='permit').map(gOf))].sort((a,b)=>a-b).forEach(g=>{
      const job=items.find(it=>it.type==='job'&&gOf(it)===g);
      const rest=items.filter(it=>it.type!=='job'&&it.type!=='permit'&&gOf(it)===g);
      const flat=job&&job.mode==='flat';
      const title=String(job&&job.desc||'').trim();
      if(title) out.push(
        <tr key={'h'+g}>
          <td colSpan={flat?4:5} style={{...st.td,fontWeight:600,paddingTop:14}}>{title}</td>
          {flat&&<td style={{...st.td,textAlign:"right",fontWeight:600,paddingTop:14}}>${(Number(job.rate)||0).toFixed(2)}</td>}
        </tr>
      );
      // A flat job prints its price only. Its lines survive the switch as the
      // record of what was used, carry rate 0 and are not billed. The filter is
      // on the AMOUNT, not on the mode, which is what keeps the printed items
      // adding up to the total: a legacy invoice saved before the switch stopped
      // deleting lines, or a receipt pushed onto a flat job, still has a
      // non-zero amount and still prints.
      rest.filter(it=>!flat||(Number(it.qty)||0)*(Number(it.rate)||0)!==0).forEach((item,i)=>out.push(row(item,g+'-'+i)));
    });
    items.filter(it=>it.type==='permit').forEach((p,i)=>out.push(row(p,'pm'+i)));
    return out;
  };
  const tpl=template||'standard';

  const Header=()=>(
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
      <div>
        <div style={{fontSize:20,fontWeight:500,color:D.accent}}>⚡ {ci.name}</div>
        <div style={{color:D.muted,fontSize:12,lineHeight:1.5}}>{addrLines(ci.address).map((l,ix)=><div key={"a"+ix}>{l}</div>)}{(ci.licenseNum||ci.phone)&&<div>{[ci.licenseNum&&`Lic# ${ci.licenseNum}`, ci.phone&&formatPhone(ci.phone)].filter(Boolean).join(" · ")}</div>}</div>
      </div>
      <div style={{textAlign:"right",display:"flex",flexDirection:"column",gap:3}}>
        <div style={{fontSize:16,fontWeight:500}}>{doc.notes?.split('\n')[0]?.slice(0,55)||`${type} ${doc.number}`}</div>
        {doc.notes&&<div style={{fontSize:11,color:D.muted}}>{type} {doc.number}</div>}
        <div style={{fontSize:12,color:D.muted}}>Date Prepared: {fmtDate(doc.date)}</div>
        {extra?.dueDate&&<div style={{fontSize:12,color:D.muted}}>Due: {fmtDate(extra.dueDate)}</div>}
        {extra?.paidDate&&<div style={{fontSize:12,color:D.success}}>Paid: {fmtDate(extra.paidDate)}</div>}
      </div>
    </div>
  );

  const BillTo=()=>(
    <div style={{marginBottom:16,padding:"10px 14px",background:D.surface,borderRadius:8}}>
      <div style={{fontSize:11,color:D.muted}}>Bill to</div>
      <div style={{fontWeight:500}}>{clientName}</div>
    </div>
  );

  const Totals=()=>(
    <div style={{marginLeft:"auto",width:220,marginTop:8}}>
      {[["Subtotal",sub],...(tax>0?[[taxLabel(),tax]]:[]),["Total",total]].map(([label,val],i)=>(
        <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderTop:i===2?`1px solid ${D.border}`:"none",fontWeight:i===2?500:400,fontSize:i===2?15:13}}>
          <span style={{color:i===2?D.text:D.muted}}>{label}</span>
          <span style={{color:i===2?D.success:D.text}}>${Number(val).toFixed(2)}</span>
        </div>
      ))}
      {type==="Estimate"&&doc.depositRequired&&doc.depositAmount&&(()=>{
        const depositAmt=doc.depositType==="percent"?total*(parseFloat(doc.depositAmount)||0)/100:parseFloat(doc.depositAmount)||0;
        return(<div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderTop:"2px solid "+D.accent,marginTop:4,fontSize:13,fontWeight:700}}>
          <span style={{color:D.accent}}>Deposit Required</span>
          <span style={{color:D.accent}}>${depositAmt.toFixed(2)}</span>
        </div>);
      })()}
    </div>
  );

  const PaymentTerms=()=>(
    <div style={{marginTop:16,padding:"10px 14px",background:D.surface,borderRadius:8,fontSize:11,color:D.muted,lineHeight:1.6}}>
      <strong style={{color:D.text}}>Payment Terms:</strong> {ci.paymentTerms || defaultPaymentTerms(ci)}
      {type==="Invoice"&&pmList.length>0&&<div style={{marginTop:8}}><strong style={{color:D.text}}>Payment Methods Accepted:</strong> {pmList.join(" · ")}</div>}
      <div style={{marginTop:8}}><strong style={{color:D.text}}>Scope Note:</strong> {ci.scopeNote || DEFAULT_SCOPE_NOTE}</div>
    </div>
  );

  const SignatureBlock=()=>(
    type==="Estimate"?<div style={{marginTop:24,display:"grid",gridTemplateColumns:"1fr 1fr",gap:32}}>
      <div>
        <div style={{fontFamily:"'Dancing Script','Brush Script MT',cursive",fontSize:32,color:D.text,height:48,display:"flex",alignItems:"flex-end",paddingBottom:4}}>{ci.ownerName||ci.name}</div>
        <div style={{borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Authorized Signature · {ci.name}</div>
        <div style={{marginTop:16,borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>{doc.date}</div>
      </div>
      <div>
        <div style={{height:48}}></div>
        <div style={{borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Client Signature · Acceptance of Estimate</div>
        <div style={{marginTop:16,borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Date</div>
      </div>
      <div style={{gridColumn:"1/-1",fontSize:11,color:D.muted,lineHeight:1.6}}>By signing above, the client agrees to the scope of work, pricing, and payment terms outlined in this estimate.</div>
    </div>:null
  );

  // ── STANDARD template ────────────────────────────────────────────────────
  if(tpl==='standard') return(
    <div style={{...st.card,maxWidth:700}}>
      <Header/>
      <BillTo/>
      {doc.notes&&<div style={{marginBottom:14,fontSize:13,color:D.muted}}>Note: {doc.notes}</div>}
      <div style={{overflowX:"auto"}}><table style={{...st.table,marginBottom:12}}>
        <thead><tr>{["Description","Qty","Unit","Rate","Amount"].map(h=><th key={h} style={{...st.th,textAlign:h==="Amount"||h==="Rate"?"right":"left"}}>{h}</th>)}</tr></thead>
        <tbody>{jobRows()}</tbody>
      </table></div>
      <Totals/>
      <PaymentTerms/>
      <SignatureBlock/>
    </div>
  );

  // ── DETAILED template ────────────────────────────────────────────────────
  if(tpl==='detailed'){
    const laborItems=items.filter(i=>i.unit==='hr'||(i.desc||'').toLowerCase().includes('labor'));
    const matItems=items.filter(i=>!laborItems.includes(i)&&!(i.desc||'').toLowerCase().includes('permit')&&!(i.desc||'').toLowerCase().includes('overhead'));
    const permitItems=items.filter(i=>(i.desc||'').toLowerCase().includes('permit'));
    const otherItems=items.filter(i=>!laborItems.includes(i)&&!matItems.includes(i)&&!permitItems.includes(i));
    const Section=({title,rows,color})=>rows.length?(<>
      <div style={{fontSize:12,fontWeight:700,color:color||D.accent,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8,marginTop:16,paddingBottom:4,borderBottom:"1px solid "+D.border}}>{title}</div>
      <div style={{overflowX:"auto"}}><table style={{...st.table,marginBottom:4}}>
        <thead><tr>{["Description","Qty","Unit","Rate","Amount"].map(h=><th key={h} style={{...st.th,textAlign:h==="Amount"||h==="Rate"?"right":"left"}}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((item,i)=><tr key={i}><td style={st.td}>{item.desc}</td><td style={st.td}>{item.qty}</td><td style={st.td}>{item.unit}</td><td style={{...st.td,textAlign:"right"}}>${Number(item.rate).toFixed(2)}</td><td style={{...st.td,textAlign:"right",fontWeight:500}}>${(item.qty*item.rate).toFixed(2)}</td></tr>)}</tbody>
      </table></div>
    </>):null;
    return(
      <div style={{...st.card,maxWidth:700}}>
        <Header/>
        <BillTo/>
        {doc.notes&&<div style={{marginBottom:14,fontSize:13,color:D.muted,padding:"10px 14px",background:D.surface,borderRadius:8}}><strong style={{color:D.text}}>Scope: </strong>{doc.notes}</div>}
        <Section title="Labor" rows={laborItems} color={D.warning}/>
        <Section title="Materials" rows={matItems} color={D.accent}/>
        <Section title="Permits & Fees" rows={permitItems} color="#a855f7"/>
        <Section title="Other" rows={otherItems} color={D.muted}/>
        <Totals/>
        <PaymentTerms/>
        <SignatureBlock/>
      </div>
    );
  }

  // ── SIMPLE template ──────────────────────────────────────────────────────
  if(tpl==='simple') return(
    <div style={{...st.card,maxWidth:700}}>
      <Header/>
      <BillTo/>
      <div style={{padding:"16px 20px",background:D.surface,borderRadius:10,marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:700,color:D.text,marginBottom:8}}>Scope of Work</div>
        <div style={{fontSize:13,color:D.muted,lineHeight:1.7}}>{doc.notes||items.map(i=>i.desc).filter(Boolean).join(', ')||'Electrical services as discussed.'}</div>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",background:"linear-gradient(135deg,"+D.accent+"22,"+D.success+"11)",border:`1px solid ${D.accent}44`,borderRadius:10,marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:700,color:D.text}}>Total — All inclusive</div>
        <div style={{fontSize:28,fontWeight:700,color:D.success}}>${total.toFixed(2)}</div>
      </div>
      <div style={{fontSize:11,color:D.muted,marginBottom:12}}>Includes all labor, materials, equipment and permit fees.{getTaxRate()>0?` ${taxLabel()} included.`:""}</div>
      <PaymentTerms/>
      <SignatureBlock/>
    </div>
  );

  // ── PROPOSAL template ────────────────────────────────────────────────────
  if(tpl==='proposal') return(
    <div style={{...st.card,maxWidth:700}}>
      <div style={{borderBottom:"2px solid "+D.accent,paddingBottom:16,marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:24,fontWeight:700,color:D.accent}}>⚡ {ci.name}</div>
            {ci.licenseNum&&<div style={{color:D.muted,fontSize:12,marginTop:2}}>Licensed Electrical Contractor · Lic# {ci.licenseNum}</div>}
            <div style={{color:D.muted,fontSize:12,lineHeight:1.5}}>{addrLines(ci.address).map((l,ix)=><div key={"p"+ix}>{l}</div>)}{ci.phone&&<div>{formatPhone(ci.phone)}</div>}</div>
          </div>
          <div style={{background:D.surface,borderRadius:8,padding:"10px 16px",textAlign:"right"}}>
            <div style={{fontSize:11,color:D.muted,textTransform:"uppercase",letterSpacing:"0.5px"}}>Proposal</div>
            <div style={{fontSize:18,fontWeight:700}}>{doc.number}</div>
            <div style={{fontSize:12,color:D.muted}}>Date Prepared: {fmtDate(doc.date)}</div>
            {extra?.dueDate&&<div style={{fontSize:12,color:D.muted}}>Valid until: {fmtDate(extra.dueDate)}</div>}
          </div>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
        <div style={{background:D.surface,borderRadius:8,padding:"12px 16px"}}>
          <div style={{fontSize:11,color:D.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Prepared for</div>
          <div style={{fontWeight:700,fontSize:15}}>{clientName}</div>
        </div>
        <div style={{background:D.surface,borderRadius:8,padding:"12px 16px"}}>
          <div style={{fontSize:11,color:D.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Prepared by</div>
          <div style={{fontWeight:700,fontSize:15}}>{ci.ownerName||ci.name}</div>
        </div>
      </div>

      {doc.notes&&<div style={{marginBottom:20,padding:"14px 18px",background:D.surface,borderRadius:8,borderLeft:"3px solid "+D.accent}}>
        <div style={{fontSize:12,fontWeight:700,color:D.accent,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.5px"}}>Project Description</div>
        <div style={{fontSize:13,color:D.text,lineHeight:1.7}}>{doc.notes}</div>
      </div>}

      <div style={{marginBottom:20}}>
        <div style={{fontSize:12,fontWeight:700,color:D.text,marginBottom:10,textTransform:"uppercase",letterSpacing:"0.5px",borderBottom:"1px solid "+D.border,paddingBottom:6}}>Scope of Work & Pricing</div>
        <div style={{overflowX:"auto"}}><table style={{...st.table,marginBottom:4}}>
          <thead><tr>{["Description","Qty","Unit","Rate","Amount"].map(h=><th key={h} style={{...st.th,textAlign:h==="Amount"||h==="Rate"?"right":"left"}}>{h}</th>)}</tr></thead>
          <tbody>{jobRows()}</tbody>
        </table></div>
        <Totals/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        <div style={{background:D.surface,borderRadius:8,padding:"12px 16px"}}>
          <div style={{fontSize:12,fontWeight:700,color:D.text,marginBottom:6}}>Payment Schedule</div>
          <div style={{fontSize:12,color:D.muted,lineHeight:1.6}}>
            {doc.depositRequired&&doc.depositAmount?(()=>{
              const d2=doc.depositType==="percent"?total*(parseFloat(doc.depositAmount)||0)/100:parseFloat(doc.depositAmount)||0;
              return`50% deposit ($${d2.toFixed(2)}) due at start · Balance due upon completion.`;
            })():"Payment due upon completion of work."}
          </div>
        </div>
        <div style={{background:D.surface,borderRadius:8,padding:"12px 16px"}}>
          <div style={{fontSize:12,fontWeight:700,color:D.text,marginBottom:6}}>Terms</div>
          <div style={{fontSize:12,color:D.muted,lineHeight:1.6}}>All work performed to NEC 2023 standards. Permits obtained as required. 1-year labor warranty.</div>
        </div>
      </div>

      <div style={{marginTop:24,display:"grid",gridTemplateColumns:"1fr 1fr",gap:32,paddingTop:16,borderTop:"1px solid "+D.border}}>
        <div>
          <div style={{fontFamily:"'Dancing Script','Brush Script MT',cursive",fontSize:32,color:D.text,height:48,display:"flex",alignItems:"flex-end",paddingBottom:4}}>{ci.ownerName||ci.name}</div>
          <div style={{borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Contractor Signature · {ci.name}</div>
          <div style={{marginTop:16,borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>{doc.date}</div>
        </div>
        <div>
          <div style={{height:48}}></div>
          <div style={{borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Client Signature · Acceptance of Proposal</div>
          <div style={{marginTop:16,borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Date</div>
        </div>
        <div style={{gridColumn:"1/-1",fontSize:11,color:D.muted,lineHeight:1.6}}>By signing above, the client accepts this proposal and authorizes {ci.name} to proceed with the described work under the terms stated above.</div>
      </div>
    </div>
  );

  // fallback
  return null;
}



export function EstimateModal({data,clients,onSave,onClose,matPrices,permits,apiSaveClient,setClients}){
  const [d,setD]=useState(()=>{const si=safeItems(data.items);const jL=si.find(i=>i._labor&&/journeyman/i.test(i.desc));const aL=si.find(i=>i._labor&&/apprentice|helper/i.test(i.desc));const mL=si.find(i=>i._labor&&/master/i.test(i.desc));return{...data,items:si.filter(i=>!i._labor),materials:toArr(data.materials),estType:data.estType||'fixed',labor:data.labor||{jHrs:jL?jL.qty:0,jRate:jL?jL.rate:75,aHrs:aL?aL.qty:0,aRate:aL?aL.rate:50,mHrs:mL?mL.qty:0,mRate:mL?mL.rate:125}};});
  const set=(k,v)=>setD(x=>({...x,[k]:v}));
  const setLabor=(k,v)=>setD(x=>({...x,labor:{...x.labor,[k]:v}}));
  const [matPicker,setMatPicker]=useState(false);
  const [matCategory,setMatCategory]=useState(Object.keys(getMAT_CATALOG())[0]);
  const [activeLineIdx,setActiveLineIdx]=useState(null);
  const [ncModal,setNcModal]=useState(false);
  const [ncData,setNcData]=useState({name:"",email:"",phone:"",address:""});
  const [ncSaving,setNcSaving]=useState(false);
  const saveNC=async()=>{
    if(!ncData.name.trim())return;
    setNcSaving(true);
    try{const c=await apiSaveClient(ncData);set("clientId",c.id);setNcModal(false);setNcData({name:"",email:"",phone:"",address:""});}
    catch(e){alert("Failed: "+e.message);}
    setNcSaving(false);
  };

  const getPrice=desc=>{
    if(matPrices&&matPrices[desc]!==undefined)return matPrices[desc];
    const found=getMAT_CATALOG_FLAT().find(m=>m.desc===desc);
    return found?found.cost:0;
  };

  // Materials linked to each line item
  const addMat=(lineIdx,item)=>{
    const price=getPrice(item.desc);
    const newMat={id:Date.now(),lineIdx,desc:item.desc,qty:1,unit:item.unit,cost:price};
    setD(x=>({...x,materials:[...toArr(x.materials),newMat]}));
    setMatPicker(false);
  };
  const updateMat=(id,k,v)=>setD(x=>({...x,materials:toArr(x.materials).map(m=>m.id===id?{...m,[k]:k==="qty"||k==="cost"?parseFloat(v)||0:v}:m)}));
  const removeMat=id=>setD(x=>({...x,materials:toArr(x.materials).filter(m=>m.id!==id)}));

  // Calculations
  const matForLine=lineIdx=>toArr(d.materials).filter(m=>m.lineIdx===lineIdx);
  const matActualForLine=lineIdx=>matForLine(lineIdx).reduce((s,m)=>s+(Number(m.qty)||0)*(Number(m.cost)||0),0);
  const matBilledForLine=lineIdx=>matActualForLine(lineIdx)*(1+EST_MARKUP/100);
  const totalMatActual=toArr(d.items).reduce((_,item,i)=>_+matActualForLine(i),0);
  const totalMatBilled=toArr(d.items).reduce((_,item,i)=>_+matBilledForLine(i),0);
  const laborBoxTotal=d.estType==='tnm'?((Number(d.labor?.jHrs)||0)*(Number(d.labor?.jRate)||75)+(Number(d.labor?.aHrs)||0)*(Number(d.labor?.aRate)||45)+(Number(d.labor?.mHrs)||0)*(Number(d.labor?.mRate)||125)):0;
  const laborTotal=calcSubtotal(d.items)+laborBoxTotal;
  const grandTotal=(laborTotal+totalMatBilled)*1.06;
  const yourCost=totalMatActual; // labor cost not tracked here
  const profit=totalMatBilled-totalMatActual;

  // Save — add a materials line item automatically to the estimate items
  const handleSave=()=>{
    let items=[...toArr(d.items)];
    if(d.estType==='tnm'){
      if(Number(d.labor?.mHrs)>0)items.unshift({desc:"Master Labor",qty:Number(d.labor.mHrs),unit:"hr",rate:Number(d.labor.mRate)||125,_labor:true});
      if(Number(d.labor?.aHrs)>0)items.unshift({desc:"Apprentice Labor",qty:Number(d.labor.aHrs),unit:"hr",rate:Number(d.labor.aRate)||50,_labor:true});
      if(Number(d.labor?.jHrs)>0)items.unshift({desc:"Journeyman Labor",qty:Number(d.labor.jHrs),unit:"hr",rate:Number(d.labor.jRate)||75,_labor:true});
    }
    // Add materials as a separate line item if any materials exist
    if(toArr(d.materials).length>0){
      // Remove any existing auto-generated materials line
      items=items.filter(i=>!i._autoMat);
      items.push({desc:"Materials",qty:1,unit:"lot",rate:Math.round(totalMatBilled*100)/100,_autoMat:true});
    }
    onSave({...d,items,materials:toArr(d.materials),labor:d.labor});
  };

  return(<Overlay><div style={{background:D.card,border:"1px solid "+D.border,borderRadius:12,padding:24,width:"min(700px,97vw)",maxHeight:"92vh",overflowY:"auto"}}>
    <MHdr title={d.id?"Edit Estimate":"New Estimate"} onClose={onClose}/>
    <div style={{display:"flex",gap:8,marginBottom:16}}>
      {[{v:"fixed",label:"Fixed Price"},{v:"tnm",label:"Time & Material"}].map(t=>(
        <button key={t.v} style={{flex:1,padding:"8px 0",borderRadius:8,border:"1px solid "+(d.estType===t.v?D.accent:D.border),background:d.estType===t.v?D.accent+"22":"transparent",color:d.estType===t.v?D.accent:D.muted,fontWeight:d.estType===t.v?600:400,cursor:"pointer",fontSize:13}} onClick={()=>set("estType",t.v)}>{t.label}</button>
      ))}
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:14}}>
      <Field label="Estimate #"><input style={st.input} value={d.number||""} onChange={e=>set("number",e.target.value)}/></Field>
      <Field label="Date"><input style={st.input} type="date" value={d.date||""} onChange={e=>set("date",e.target.value)}/></Field>
      <Field label="Status"><select style={st.input} value={d.status} onChange={e=>{
        const newStatus=e.target.value;
        if(newStatus==="Approved"&&d.depositRequired&&d.depositAmount){
          const depositAmt=d.depositType==="percent"?calcTotal(d.items)*(parseFloat(d.depositAmount)||0)/100:parseFloat(d.depositAmount)||0;
          const msg="This estimate requires a deposit of $"+depositAmt.toFixed(2)+".\n\nHave you received the deposit?\n\nOK = approve anyway\nCancel = record deposit first";
          if(!window.confirm(msg))return;
        }
        set("status",newStatus);
      }}>{["Draft","Sent","Approved"].map(v=><option key={v}>{v}</option>)}</select></Field>
      <div style={{gridColumn:"1/-1"}}><Field label="Client">
        <NewClientInline clients={clients} value={d.clientId} onChange={v=>set("clientId",v)}
          onNewClient={async name=>{
            try{const created=await apiPost("/data/clients",{name});
            if(setClients)setClients(prev=>[...toArr(prev),created]);
            set("clientId",created.id);}catch(e){alert("Failed: "+e.message);}
          }}/>
      </Field></div>
      <div style={{gridColumn:"1/-1"}}><Field label="Notes / Scope"><input style={st.input} value={d.notes||""} onChange={e=>set("notes",e.target.value)}/></Field></div>
    </div>

    {/* Line Items */}
    <div style={{marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:500,color:D.text,marginBottom:8}}>{d.estType==='tnm'?"Labor Hours":"Line Items"}</div>
      {d.estType==='tnm'&&<>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
        {[{k:"jHrs",rk:"jRate",label:"Journeyman",dRate:75},{k:"aHrs",rk:"aRate",label:"Apprentice",dRate:50},{k:"mHrs",rk:"mRate",label:"Master Electrician",dRate:125}].map(r=>(
          <div key={r.k} style={{background:D.surface,borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontSize:11,color:D.muted,marginBottom:6}}>{r.label}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              <Field label="Hours"><input style={st.input} type="number" min="0" step="0.5" value={d.labor?.[r.k]??0} onChange={e=>setLabor(r.k,parseFloat(e.target.value)||0)}/></Field>
              <Field label="Bill/hr ($)"><input style={st.input} type="number" min="0" value={d.labor?.[r.rk]??r.dRate} onChange={e=>setLabor(r.rk,parseFloat(e.target.value)||0)}/></Field>
            </div>
            {(Number(d.labor?.[r.k])||0)>0&&<div style={{fontSize:11,color:D.success,marginTop:4}}>Billed: {fmt((Number(d.labor[r.k])||0)*(Number(d.labor[r.rk])||r.dRate))}</div>}
          </div>
        ))}
      </div>
      <div style={{fontSize:13,fontWeight:500,color:D.text,marginBottom:8,marginTop:16,paddingTop:16,borderTop:"1px solid "+D.border}}>Services / Line Items</div>
      </>}
      <div style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 32px",gap:6,marginBottom:4}}>
        {["Description","Qty","Unit","Rate ($)",""].map(h=><div key={h} style={{fontSize:11,color:D.muted}}>{h}</div>)}
      </div>
      {toArr(d.items).filter(i=>!i._autoMat).map((item,i)=>(
        <div key={i}>
          <div style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 32px",gap:6,marginBottom:4}}>
            <input style={st.input} value={item.desc||""} onChange={e=>setD(x=>({...x,items:x.items.map((it,idx)=>idx===i?{...it,desc:e.target.value}:it)}))} placeholder="e.g. Add outlet, Troubleshoot, etc."/>
            <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.qty} onChange={e=>setD(x=>({...x,items:x.items.map((it,idx)=>idx===i?{...it,qty:parseFloat(e.target.value)||0}:it)}))}/>
            <select style={st.input} value={item.unit||"hr"} onChange={e=>setD(x=>({...x,items:x.items.map((it,idx)=>idx===i?{...it,unit:e.target.value}:it)}))}>{UNITS.map(u=><option key={u}>{u}</option>)}</select>
            <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.rate} onChange={e=>setD(x=>({...x,items:x.items.map((it,idx)=>idx===i?{...it,rate:parseFloat(e.target.value)||0}:it)}))}/>
            <button style={{background:D.danger,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontSize:14,padding:"0 8px"}} onClick={()=>setD(x=>({...x,items:x.items.filter((_,idx)=>idx!==i),materials:toArr(x.materials).filter(m=>m.lineIdx!==i)}))}>✕</button>
          </div>
          {/* Materials for this line item — hidden for Permit lines */}
          {!/^permit$/i.test((item.desc||"").trim())&&<div style={{marginLeft:16,marginBottom:8,padding:"8px 12px",background:D.surface,borderRadius:7,border:`1px solid ${D.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:matForLine(i).length?8:0}}>
              <div style={{fontSize:11,color:D.muted}}>Materials for "{item.desc||"this item"}"</div>
              <button style={{...st.btn("primary"),marginLeft:0,padding:"3px 10px",fontSize:11}} onClick={()=>{setActiveLineIdx(i);setMatPicker(true);}}>+ Add Material</button>
            </div>
            {matForLine(i).length>0&&<>
              {matForLine(i).map(m=>(
                <div key={m.id} style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.2fr 1.2fr 28px",gap:6,marginBottom:4,alignItems:"center"}}>
                  <div style={{fontSize:12,color:D.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.desc}</div>
                  <input style={{...st.input,fontSize:11}} type="number" min="0" value={m.qty} onChange={e=>updateMat(m.id,"qty",e.target.value)}/>
                  <div style={{fontSize:11,color:D.muted}}>{m.unit}</div>
                  <div style={{fontSize:11,color:D.danger}}>${Number(m.cost).toFixed(2)}</div>
                  <div style={{fontSize:11,color:D.success}}>${(Number(m.qty)*Number(m.cost)*(1+EST_MARKUP/100)).toFixed(2)}</div>
                  <button style={{background:D.danger,color:"#fff",border:"none",borderRadius:5,cursor:"pointer",fontSize:12,padding:"0 6px",height:24}} onClick={()=>removeMat(m.id)}>✕</button>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,paddingTop:6,borderTop:`1px solid ${D.border}`}}>
                <span style={{color:D.muted}}>Your cost: <span style={{color:D.danger}}>{fmt(matActualForLine(i))}</span></span>
                <span style={{color:D.muted}}>Billed ({EST_MARKUP}% markup): <span style={{color:D.success,fontWeight:600}}>{fmt(matBilledForLine(i))}</span></span>
              </div>
            </>}
          </div>}
        </div>
      ))}
      <button style={{...st.btn(),marginLeft:0,marginTop:4}} onClick={()=>setD(x=>({...x,items:[...x.items.filter(i=>!i._autoMat),{desc:"",qty:1,unit:"ea",rate:""}]}))}>+ Add Line Item</button>
      <button style={{...st.btn(),marginLeft:0,marginTop:6,display:"block"}} onClick={()=>setD(x=>({...x,items:[...x.items.filter(i=>!i._autoMat),{desc:"Permit",qty:1,unit:"ea",rate:500}]}))}>+ Permit</button>

      <div style={{marginTop:16,display:"flex",alignItems:"flex-start",gap:12,padding:"12px 14px",background:D.surface,borderRadius:8,border:`1px solid ${D.border}`,flexWrap:"wrap"}}>
        {/* Deposit prompt */}
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:12,fontWeight:600,color:D.text,marginBottom:6}}>💰 Request Deposit?</div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,color:D.muted}}>
              <input type="checkbox" checked={!!d.depositRequired} onChange={e=>set("depositRequired",e.target.checked)} style={{accentColor:D.accent}}/>
              Yes, request a deposit
            </label>
          </div>
          {d.depositRequired&&<div style={{display:"flex",gap:8,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
            <select style={{...st.input,width:110,fontSize:12}} value={d.depositType||"percent"} onChange={e=>set("depositType",e.target.value)}>
              <option value="percent">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
            <input style={{...st.input,width:90,fontSize:12}} type="number" min="0" step={d.depositType==="percent"?"1":"0.01"} value={d.depositAmount||""} onChange={e=>set("depositAmount",e.target.value)} placeholder={d.depositType==="percent"?"%":"$"}/>
            {d.depositType==="percent"&&grandTotal>0&&d.depositAmount&&<span style={{fontSize:12,color:D.success,fontWeight:600}}>{fmt(grandTotal*(parseFloat(d.depositAmount)||0)/100)} due upfront</span>}
            {d.depositType==="fixed"&&<span style={{fontSize:12,color:D.success,fontWeight:600}}>{fmt(parseFloat(d.depositAmount)||0)} due upfront</span>}
          </div>}
        </div>
        {/* Totals */}
        <div style={{textAlign:"right",fontSize:12}}>
          <div style={{color:D.muted}}>Labor: <strong style={{color:D.text}}>{fmt(laborTotal)}</strong></div>
          <div style={{color:D.muted}}>Materials: <strong style={{color:D.text}}>{fmt(totalMatBilled)}</strong></div>
          {getTaxRate()>0&&<div style={{color:D.muted}}>{taxLabel()}: <strong style={{color:D.text}}>{fmt(calcTax(laborTotal+totalMatBilled))}</strong></div>}
          <div style={{color:D.success,fontWeight:700,fontSize:14,marginTop:4}}>Total: {fmt(grandTotal)}</div>
          {d.depositRequired&&d.depositAmount&&<div style={{color:D.accent,fontWeight:600,marginTop:2}}>
            Deposit: {fmt(d.depositType==="percent"?grandTotal*(parseFloat(d.depositAmount)||0)/100:parseFloat(d.depositAmount)||0)}
          </div>}
        </div>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
        <button style={st.btn()} onClick={onClose}>Cancel</button>
        <button style={st.btn("primary")} onClick={handleSave}>Save Estimate</button>
      </div>
    </div>

    {/* Profit snapshot */}
    {toArr(d.materials).length>0&&<div style={{padding:"12px 16px",background:"#1a2a1a",borderRadius:8,border:`1px solid ${D.success}33`,marginBottom:16,display:"flex",gap:20,flexWrap:"wrap"}}>
      <div><div style={{fontSize:10,color:D.muted}}>Labor billed</div><div style={{fontSize:13,fontWeight:600,color:D.text}}>{fmt(laborTotal)}</div></div>
      <div><div style={{fontSize:10,color:D.muted}}>Mat. cost</div><div style={{fontSize:13,fontWeight:600,color:D.danger}}>{fmt(totalMatActual)}</div></div>
      <div><div style={{fontSize:10,color:D.muted}}>Mat. billed ({EST_MARKUP}%)</div><div style={{fontSize:13,fontWeight:600,color:D.success}}>{fmt(totalMatBilled)}</div></div>
      <div><div style={{fontSize:10,color:D.muted}}>Mat. profit</div><div style={{fontSize:13,fontWeight:600,color:D.warning}}>{fmt(profit)}</div></div>
      <div><div style={{fontSize:10,color:D.muted}}>Grand total (w/tax)</div><div style={{fontSize:15,fontWeight:700,color:D.success}}>{fmt(grandTotal)}</div></div>
    </div>}

    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
      <div style={{color:D.success,fontWeight:500,fontSize:12}}>
        Labor: {fmt(laborTotal)} | Materials: {fmt(totalMatBilled)}{getTaxRate()>0?` | Tax: ${fmt(calcTax(laborTotal+totalMatBilled))}`:""} | Total: {fmt(grandTotal)}
      </div>
      <div><button style={st.btn()} onClick={onClose}>Cancel</button><button style={st.btn("primary")} onClick={handleSave}>Save</button></div>
    </div>

    {/* Material catalog picker */}
    {matPicker&&<Overlay zIndex={200}><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(600px,97vw)",maxHeight:"85vh",overflowY:"auto"}}>
      <MHdr title="Add Material" onClose={()=>setMatPicker(false)}/>
      <div style={{fontSize:12,color:D.muted,marginBottom:12}}>Materials will be marked up {EST_MARKUP}% for the customer.</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
        {Object.keys(getMAT_CATALOG()).map(cat=>(
          <button key={cat} style={{...st.btn(),marginLeft:0,padding:"4px 10px",fontSize:11,background:matCategory===cat?D.accent:D.border,color:matCategory===cat?"#fff":D.text}} onClick={()=>setMatCategory(cat)}>{cat}</button>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {(getMAT_CATALOG()[matCategory]||[]).map(item=>{
          const cost=getPrice(item.desc);
          const billed=cost*(1+EST_MARKUP/100);
          return(
            <div key={item.desc} style={{background:D.surface,borderRadius:7,padding:"8px 12px",cursor:"pointer",border:`1px solid ${D.border}`}} onClick={()=>addMat(activeLineIdx,item)}>
              <div style={{fontSize:12,fontWeight:500,marginBottom:4}}>{item.desc}</div>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <span style={{fontSize:11,color:D.danger}}>Cost: ${cost.toFixed(2)}/{item.unit}</span>
                <span style={{fontSize:11,color:D.success,fontWeight:500}}>Bill: ${billed.toFixed(2)}/{item.unit}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div></Overlay>}
  </div></Overlay>);
}



export function PhotoPanel({jobId,photos,onAdd,onDelete}){
  const [uploading,setUploading]=useState(false);
  const [lightbox,setLightbox]=useState(null);
  const [caption,setCaption]=useState("");
  const fileRef=useRef();

  const handleFiles=async e=>{
    const files=[...e.target.files];
    if(!files.length)return;
    setUploading(true);
    for(const file of files){
      await onAdd(jobId,file,caption).catch(err=>console.warn("Photo upload failed:",err));
    }
    setCaption("");
    setUploading(false);
    e.target.value="";
  };

  return(
    <div style={{borderTop:"1px solid "+D.border,paddingTop:12,marginTop:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:13,fontWeight:500,color:D.muted}}>📷 Photos {photos.length>0&&"("+photos.length+")"}</div>
        <button style={{...st.btn(),marginLeft:0,padding:"4px 10px",fontSize:11}} onClick={()=>fileRef.current?.click()} disabled={uploading}>
          {uploading?"Uploading...":"+ Add Photo"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={handleFiles}/>
      </div>

      {photos.length>0&&(
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
          {photos.map(p=>(
            <div key={p.id} style={{position:"relative",width:80,height:80,flexShrink:0}}>
              <img src={p.url} alt={p.caption||"Job photo"} onClick={()=>setLightbox(p)}
                style={{width:80,height:80,objectFit:"cover",borderRadius:8,cursor:"pointer",border:"1px solid "+D.border}}/>
              <button onClick={()=>onDelete(jobId,p.id)}
                style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",background:D.danger,border:"none",color:"#fff",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox&&<div onClick={()=>setLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div onClick={e=>e.stopPropagation()} style={{maxWidth:"90vw",maxHeight:"90vh"}}>
          <img src={lightbox.url} alt={lightbox.caption} style={{maxWidth:"100%",maxHeight:"80vh",borderRadius:10,objectFit:"contain"}}/>
          {lightbox.caption&&<div style={{color:"#fff",textAlign:"center",marginTop:8,fontSize:13}}>{lightbox.caption}</div>}
          <div style={{color:"#888",textAlign:"center",fontSize:11,marginTop:4}}>
            {lightbox.filename}
            {lightbox.originalSize&&lightbox.compressedSize&&" · "+lightbox.originalSize+" → "+lightbox.compressedSize}
            {" · "}{new Date(lightbox.addedAt).toLocaleDateString()}
          </div>
        </div>
      </div>}
    </div>
  );
}


export function DocumentPanel({bidId, documents, onAdd, onDelete}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const handleFile = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { alert('Only PDF files are accepted.'); return; }
    if (file.size > 25 * 1024 * 1024) { alert('File must be under 25 MB.'); return; }
    setUploading(true);
    try {
      await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async ev => {
          try { await onAdd(bidId, {name: file.name, data: ev.target.result, size: file.size}); resolve(); }
          catch(err) { reject(err); }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    } catch(err) { alert('Upload failed: ' + err.message); }
    setUploading(false);
    e.target.value = '';
  };
  const handleView = async doc => {
    try {
      const json = await apiGet('/files/url?key=' + encodeURIComponent(doc.key));
      if (json.url) window.open(json.url, '_blank');
      else alert('Could not load file.');
    } catch(err) { alert('Could not open: ' + err.message); }
  };
  const docs = toArr(documents);
  return (
    <div style={{borderTop:`1px solid ${D.border}`,paddingTop:12,marginTop:12}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <div style={{fontSize:13,fontWeight:500,color:D.muted}}>📎 Documents {docs.length>0&&`(${docs.length})`}</div>
        <button style={{...st.btn(),marginLeft:0,padding:'4px 10px',fontSize:11}} onClick={()=>fileRef.current?.click()} disabled={uploading}>
          {uploading?'Uploading...':'+ Attach PDF'}
        </button>
        <input ref={fileRef} type="file" accept="application/pdf" style={{display:'none'}} onChange={handleFile}/>
      </div>
      {docs.length===0
        ? <div style={{color:D.muted,fontSize:12,padding:'8px 0'}}>No documents attached — add lighting package, switch gear specs, or cut sheets.</div>
        : <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {docs.map(doc=>(
              <div key={doc.id} style={{display:'flex',alignItems:'center',gap:8,background:D.surface,borderRadius:7,padding:'7px 10px'}}>
                <span style={{fontSize:16}}>📄</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.name}</div>
                  <div style={{fontSize:11,color:D.muted}}>{Math.round((doc.size||0)/1024)} KB · {doc.uploadedAt?.slice(0,10)||""}</div>
                </div>
                <button style={{...st.btn(),padding:'3px 8px',fontSize:11,marginLeft:0}} onClick={()=>handleView(doc)}>View</button>
                <button style={{...st.btn("danger"),padding:'3px 8px',fontSize:11,marginLeft:0}} onClick={()=>onDelete(bidId,doc.id,doc.key)}>X</button>
              </div>
            ))}
          </div>
      }
    </div>
  );
}

export function NewClientModal({client, onSave, onClose}) {
  const [d, setD] = React.useState(client ? {id:client.id, name:client.name||'', email:client.email||'', phone:client.phone||'', address:client.address||''} : {id:null, name:'', email:'', phone:'', address:''});
  const set = (k,v) => setD(x=>({...x,[k]:v}));
  return (
    <Overlay>
      <div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:'min(480px,95vw)'}}>
        <MHdr title={client ? "Edit Client" : "New Client"} onClose={onClose}/>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          {[['Name','name'],['Email','email'],['Phone','phone']].map(([label,key])=>(
            <Field key={key} label={label}>
              <input style={st.input}
                value={d[key]||''} onChange={e=>set(key,key==='phone'?formatPhone(e.target.value):e.target.value)}/>
            </Field>
          ))}
          <div style={{gridColumn:'1/-1'}}>
            <Field label="Address">
              <input style={st.input}
                value={d.address||''} onChange={e=>set('address',e.target.value)}/>
            </Field>
          </div>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:20}}>
          <button style={st.btn()} onClick={onClose}>Cancel</button>
          <button style={st.btn("primary")} onClick={()=>onSave({...d, name: capitalizeWords(d.name)})}>Save</button>
        </div>
      </div>
    </Overlay>
  );
}

function LineItems({initialItems, onChange, permits}) {
  const [items, setItems] = useState(() => toArr(initialItems).length ? toArr(initialItems) : [emptyItem()]);
  const update = (i,k,v) => setItems(prev => { const next = prev.map((it,idx)=>idx===i?{...it,[k]:k==="qty"||k==="rate"?parseFloat(v)||0:v}:it); onChange(next); return next; });
  const add = () => setItems(prev => { const next = [...prev, emptyItem()]; onChange(next); return next; });
  const addPermit = () => setItems(prev => { const next = [...prev, {desc:"Permit",qty:1,unit:"ea",rate:500}]; onChange(next); return next; });
  const remove = i => setItems(prev => { const next = prev.filter((_,idx)=>idx!==i); onChange(next); return next; });
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 32px",gap:6,marginBottom:4}}>{["Description","Qty","Unit","Rate ($)",""].map(h=><div key={h} style={{fontSize:11,color:D.muted}}>{h}</div>)}</div>
      {items.map((item,i)=>
        <div key={i} style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 32px",gap:6,marginBottom:6}}>
          <input style={st.input} value={item.desc||""} onChange={e=>update(i,"desc",e.target.value)} placeholder="Description"/>
          <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.qty} onChange={e=>update(i,"qty",e.target.value)}/>
          <select style={st.input} value={item.unit||"hr"} onChange={e=>update(i,"unit",e.target.value)}>{UNITS.map(u=><option key={u}>{u}</option>)}</select>
          <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.rate} onChange={e=>update(i,"rate",e.target.value)}/>
          <button style={{background:D.danger,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontSize:14,padding:"0 8px"}} onClick={()=>remove(i)}>✕</button>
        </div>
      )}
      <button style={{...st.btn(),marginLeft:0,marginTop:6,display:"block"}} onClick={add}>+ Add line item</button>
      <button style={{...st.btn(),marginLeft:0,marginTop:6,display:"block"}} onClick={addPermit}>+ Permit</button>
    </div>
  );
}

export function InvoiceModal({data, clients, jobs, expenses=[], onSave, onClose, apiSaveClient, markup, companyInfo, matPriceHistory={}, services={}, setServices=()=>{}}) {
  const [d, setD] = useState({...data, items: safeItems(data.items), jobId: data.jobId||null});
  const set = (k,v) => setD(x=>({...x,[k]:v}));
  const [ncModal, setNcModal] = useState(false);
  const [ncData, setNcData] = useState({name:"",email:"",phone:"",address:""});
  const [ncSaving, setNcSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [logImporting, setLogImporting] = useState(false);
  const receiptFileRef = useRef(null);

  // Price history and the service book arrive as props now. App loaded them at
  // startup, so this is the same data fetched once per session rather than once
  // per invoice - and after the CED import that response is the whole catalog.
  const matHistory = matPriceHistory || {};

  // items without type treated as labor (backward compat)
  const laborItems = toArr(d.items).filter(it => it.type !== 'material' && it.type !== 'job');
  const matItems   = toArr(d.items).filter(it => it.type === 'material');
  // legacy helpers kept for importLog / scan-receipt; must not drop job lines
  const syncItems  = (labor, mat) => setD(x => ({...x, items: [...toArr(x.items).filter(it=>it.type==='job'), ...labor, ...mat]}));

  const allItems = toArr(d.items);
  const setItems = next => setD(x => ({...x, items: next}));
  const gOf = it => Number(it.group)||0;
  // Permit is invoice-level, not part of any job - exclude it or it conjures a phantom job group.
  const jobGroups = (()=>{ const gs=[...new Set(allItems.filter(it=>it.type!=='permit').map(gOf))]; return gs.length?gs.sort((a,b)=>a-b):[0]; })();
  const jobLine = g => allItems.find(it=>it.type==='job'&&gOf(it)===g)||{};
  const num = (k,v) => (k==='qty'||k==='rate')?(parseFloat(v)||0):v;
  const updateIn = (pred,idx,k,v) => { let n=-1; setItems(allItems.map(it=>{ if(pred(it)){ n++; if(n===idx) return {...it,[k]:num(k,v)}; } return it; })); };
  // Patch several keys of the nth row matching pred in one update (updateIn does one key).
  const patchIn = (pred,idx,patch) => { let n=-1; setItems(allItems.map(it=>{ if(pred(it)){ n++; if(n===idx) return {...it,...patch}; } return it; })); };
  const removeIn = (pred,idx) => { let n=-1; setItems(allItems.filter(it=>{ if(pred(it)){ n++; return n!==idx; } return true; })); };
  const updateJob = (g,k,v) => setItems(allItems.map(it=> it.type==='job'&&gOf(it)===g ? {...it,[k]:num(k,v)} : it));
  const updateJobFields = (g,f) => setItems(allItems.map(it=> it.type==='job'&&gOf(it)===g ? {...it,...f} : it));
  // flat carries its own price; t&m zeroes it so materials+labor are the total
  // Switching to Flat Rate used to DELETE the job's materials and labor. That
  // deletion was itself a fix for a worse bug - the lines kept counting toward
  // the total but stopped printing - but it threw away the only record of what
  // the job used, and could not be undone. Keep the lines and park each billed
  // rate in billedRate instead. Nothing bills, because EVERY total in the app
  // and the backend (invoice, late fee, reminder balance, Stripe pay link) is a
  // sum of qty x rate and a parked line contributes 0. That is deliberately ONE
  // rule in ONE place rather than a "skip flat jobs" rule copied into the five
  // separate summing functions, where one stale copy would be a silent money
  // bug - and one of those five sets the amount charged to a customer's card.
  const setJobMode = (g,mode) => {
    const isExtra = it => it.type!=='job'&&it.type!=='permit'&&gOf(it)===g;
    if(mode==='flat'){
      // Roll the lines up into the flat price so no money silently disappears,
      // but only when the job has no price of its own - never overwrite a
      // figure somebody typed.
      const sum = Math.round(allItems.filter(isExtra).reduce((t,it)=>t+(Number(it.qty)||0)*(Number(it.rate)||0),0)*100)/100;
      setItems(allItems.map(it=>{
        if(it.type==='job'&&gOf(it)===g) return {...it,mode,qty:1,unit:'ea',rate:(Number(it.rate)||0)||sum};
        if(isExtra(it)) return {...it,billedRate:it.billedRate!==undefined?it.billedRate:(Number(it.rate)||0),rate:0};
        return it;
      }));
      return;
    }
    // Switching back restores the exact prices, which is the whole point of
    // parking them rather than zeroing them. No confirm dialog any more: it
    // existed only to warn about the deletion, and nothing is destroyed now.
    setItems(allItems.map(it=>{
      if(it.type==='job'&&gOf(it)===g) return {...it,mode,qty:1,unit:'ea',rate:0};
      if(isExtra(it)&&it.billedRate!==undefined){ const {billedRate,...rest}=it; return {...rest,rate:Number(billedRate)||0}; }
      return it;
    }));
  };
  const addJob = () => setItems([...allItems,{type:'job',group:Math.max(0,...jobGroups)+1,desc:'',mode:'tm',qty:1,unit:'ea',rate:0}]);
  // Permit fee: one per invoice, amount typed per job. Deliberately NOT a fixed
  // Settings value - unincorporated Lee is ~$197, Collier $150 min, City of
  // Naples $0.10/sqft + 6% + $60/inspection, Cape Coral $75-250.
  const permitLine = allItems.find(it=>it.type==='permit');
  const togglePermit = on => setItems(on
    ? [...allItems,{type:'permit',group:0,desc:'Permit Fee',qty:1,unit:'ea',rate:Number(markup?.permitFee)||0}]
    : allItems.filter(it=>it.type!=='permit'));
  const setPermitRate = v => setItems(allItems.map(it=> it.type==='permit' ? {...it,rate:parseFloat(v)||0} : it));
  const removeJob = g => setItems(allItems.filter(it=>gOf(it)!==g));
  const addRow = (g,type,extra={}) => setItems([...allItems,{type,group:g,desc:'',qty:1,unit:'hr',rate:0,...extra}]);

  // ---- linked materials + markup dial ----------------------------------
  // Field names are read defensively: /data/expenses is a SELECT *, so rows
  // arrive snake_case, but a future mapper could camelCase them.
  const expRows  = toArr(expenses);
  const expDesc  = e => String(e.description ?? e.desc ?? '');
  const expQty   = e => Number(e.qty) || 1;
  // unit_cost is the TAX-INCLUSIVE figure the server computed. Fall back to
  // amount/qty for legacy rows from the old aggregate scan path.
  const expCost  = e => Number(e.unit_cost ?? e.unitCost ?? 0) || (Number(e.amount || 0) / expQty(e));
  const linkedUnbilled = d.id ? expRows.filter(e =>
      String(e.linked_invoice ?? e.linkedInvoice ?? '') === String(d.id)
      // A cost deliberately marked NOT billable is never offered for billing.
      // Flat-rate work and absorbed costs both land here: the receipt stays
      // attached so P/L is right, and nothing proposes a customer-facing line.
      && !(e.billable === false || Number(e.billable) === 0)
      && !toArr(d.items).some(it => it.type === 'material' && it.desc === expDesc(e).replace(/&amp;/g, '&'))
    ) : [];

  const costedMats = toArr(d.items).filter(it => it.type === 'material' && Number(it.cost) > 0);
  const derivedPct = (() => {
    if (!costedMats.length) return null;
    const r = costedMats.map(it => Math.round((it.rate / it.cost) * 1000) / 1000);
    return r.every(v => v === r[0]) ? Math.round((r[0] - 1) * 1000) / 10 : null;
  })();
  const [matMarkupPct, setMatMarkupPct] = useState(null);
  const effMarkup = matMarkupPct !== null ? matMarkupPct
                  : derivedPct !== null ? derivedPct
                  : (Number(markup?.Materials) || 0);

  // Only lines that carry a cost can be recomputed - a hand-typed line has no
  // cost to divide back out, so it is left alone rather than guessed at.
  const applyMatMarkup = (pct) => {
    setMatMarkupPct(pct);
    setItems(allItems.map(it => (it.type === 'material' && Number(it.cost) > 0)
      ? { ...it, rate: Math.round(it.cost * (1 + (Number(pct) || 0) / 100) * 100) / 100 } : it));
  };

  const pullLinked = (g) => {
    if (!linkedUnbilled.length) return;
    setItems([...allItems, ...linkedUnbilled.map(e => {
      const cost = expCost(e);
      return { type: 'material', group: g,
               desc: expDesc(e).replace(/&amp;/g, '&'),
               qty: expQty(e), unit: e.unit || 'ea', cost,
               rate: Math.round(cost * (1 + (Number(effMarkup) || 0) / 100) * 100) / 100 };
    })]);
  };
  const addLaborRow = (g,desc,rate) => setItems([...allItems,{type:'labor',group:g,desc,qty:1,unit:'hr',rate}]);

  // every group needs a job line; legacy invoices open as one untitled job
  useEffect(()=>{
    const its=toArr(d.items);
    if(its.length===0){ setItems([{type:'job',group:0,desc:'',mode:'tm',qty:1,unit:'ea',rate:0}]); return; }
    const missing=[...new Set(its.filter(it=>it.type!=='permit').map(gOf))].filter(g=>!its.some(it=>it.type==='job'&&gOf(it)===g));
    if(missing.length) setItems([...missing.map(g=>({type:'job',group:g,desc:'',mode:'tm',qty:1,unit:'ea',rate:0})), ...its]);
  },[]);


  const updateLabor = (i, k, v) => {
    const next = laborItems.map((it, idx) => idx===i ? {...it,[k]:k==='qty'||k==='rate'?parseFloat(v)||0:v} : it);
    syncItems(next, matItems);
  };
  const removeLabor = i => syncItems(laborItems.filter((_,idx)=>idx!==i), matItems);
  const addMaster     = () => syncItems([...laborItems,{type:'labor',desc:'Master Electrician',qty:1,unit:'hr',rate:markup?.masterRate||125}], matItems);
  const addJourneyman = () => syncItems([...laborItems,{type:'labor',desc:'Journeyman',qty:1,unit:'hr',rate:markup?.journeymanRate||95}], matItems);
  const addApprentice = () => syncItems([...laborItems,{type:'labor',desc:'Apprentice',qty:1,unit:'hr',rate:(markup?.apprenticeRate ?? markup?.helperRate ?? 65)}], matItems);
  const addPermit     = () => syncItems([...laborItems,{type:'labor',desc:'Permit',qty:1,unit:'ea',rate:500}], matItems);
  const addService    = () => syncItems([...laborItems,{type:'labor',desc:'',qty:1,unit:'ea',rate:0}], matItems);
  const importLog = async () => {
    if(!d.jobId)return;
    setLogImporting(true);
    try{
      const entries=await apiGet(`/data/jobs/${d.jobId}/log`);
      const mr=markup?.masterRate||125,jr=markup?.journeymanRate||95,hr=(markup?.apprenticeRate ?? markup?.helperRate ?? 65);
      const newLaborItems=toArr(entries).flatMap(e=>{
        const rows=[];
        const desc=e.description||'';
        const date=e.log_date||'';
        if(Number(e.master_hrs)>0)rows.push({type:'labor',desc:(date+' Master'+(desc?' – '+desc:'')).trim(),qty:Number(e.master_hrs),unit:'hr',rate:mr});
        if(Number(e.journeyman_hrs)>0)rows.push({type:'labor',desc:(date+' Journeyman'+(desc?' – '+desc:'')).trim(),qty:Number(e.journeyman_hrs),unit:'hr',rate:jr});
        if(Number(e.apprentice_hrs)>0)rows.push({type:'labor',desc:(date+' Apprentice'+(desc?' – '+desc:'')).trim(),qty:Number(e.apprentice_hrs),unit:'hr',rate:hr});
        return rows;
      });
      const matText=toArr(entries).map(e=>{
        const m=e.materials;
        if(!m||m==='[]'||m==='')return null;
        try{const a=JSON.parse(m);return Array.isArray(a)&&a.length===0?null:String(m);}catch(_){return String(m).trim()||null;}
      }).filter(Boolean).join(' | ');
      const newMatItems=matText?[{type:'material',desc:'Materials: '+matText,qty:1,unit:'ea',rate:0}]:[];
      if(newLaborItems.length===0&&newMatItems.length===0){alert('No hours or materials found in daily log.');setLogImporting(false);return;}
      syncItems([...laborItems,...newLaborItems],[...matItems,...newMatItems]);
    }catch(e){alert('Failed to import: '+(e.message||e));}
    setLogImporting(false);
  };

  const updateMat = (i, k, v) => {
    let next = matItems.map((it, idx) => idx===i ? {...it,[k]:k==='qty'||k==='rate'?parseFloat(v)||0:v} : it);
    if (k === 'desc') {
      const histKey = Object.keys(effHistory).find(hk => hk.toLowerCase() === v.toLowerCase());
      const hist = histKey ? effHistory[histKey] : null;
      if (hist && hist.length > 0) {
        const latest = hist[0];
        const pct = markup?.Materials || 20;
        const billed = Math.round(Number(latest.price) * (1 + pct/100) * 100) / 100;
        next = matItems.map((it, idx) => idx===i ? {...it, desc:v, rate:billed} : it);
      }
    }
    syncItems(laborItems, next);
  };
  const removeMat = i => syncItems(laborItems, matItems.filter((_,idx)=>idx!==i));
  const addMat    = () => syncItems(laborItems, [...matItems,{type:'material',desc:'',qty:1,unit:'ea',rate:0}]);

  const effHistory = useMemo(() => ({...DEFAULT_MAT_CATALOG, ...matHistory}), [matHistory]);
  // Trade names join the suggestion list alongside the catalog names, so the
  // dropdown answers to "12/2 romex" as well as "NM12/2WG 250' COILS".
  const matSuggestions = useMemo(() => [...Object.keys(effHistory),
    ...Object.keys(MAT_ALIASES).filter(a => MAT_ALIASES[a].some(t => effHistory[t]))], [effHistory]);
  const suggestId = 'inv-mat-suggest';
  const matDatalist = useMemo(() => matSuggestions.length > 0
    ? <datalist id={suggestId}>{matSuggestions.map(m => <option key={m} value={m}/>)}</datalist>
    : null, [matSuggestions]);

  // Labor roles. Rates come from Settings > Labor & Markup Rates.
  const LABOR_OPTS = [
    {desc:"Master Electrician", rate:Number(markup?.masterRate)||125},
    {desc:"Journeyman",         rate:Number(markup?.journeymanRate)||95},
    {desc:"Apprentice",         rate:Number(markup?.apprenticeRate ?? markup?.helperRate ?? 65)},
  ];
  const isRole = v => LABOR_OPTS.some(o=>o.desc===v);
  // Picking a role carries its billing rate with it, and forces hours as the unit.
  const setLaborRole = (pred,idx,desc) => {
    const o = LABOR_OPTS.find(x=>x.desc===desc);
    patchIn(pred,idx, o?{desc:o.desc,rate:o.rate,unit:"hr"}:{desc});
  };
  // Group-aware material description handler: autofills the billed rate from
  // supply-house price history with the materials markup applied. Replaces the
  // old updateMat, which indexed the pre-job-rework flat materials list and so
  // wrote to the wrong row once an invoice could hold more than one job.
  // Resolves typed text to a price-history entry. Index 0 is the NEWEST entry -
  // every writer prepends - so this must not read from the end of the array.
  //
  // Three ways in, in order of confidence:
  //   1. the exact catalog name
  //   2. a TRADE NAME - "12/2 romex" is "NM12/2WG 250' COILS"; nobody writes
  //      the second one in the field. Where a term means several materials the
  //      regular article is first and the specialties follow.
  //   3. a unique PARTIAL - but only when exactly ONE material in the book
  //      contains the typed text. Uniqueness of a PREFIX is not enough and
  //      would be dangerous: "GFCI" is a unique prefix of "GFCI HANDYBOX
  //      COVER" while appearing inside ten other materials, so prefix matching
  //      would quietly price a GFCI receptacle as a cover.
  const MATCH_MIN = 4;
  const normKey = s => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const matMatch = v => {
    const q = normKey(v);
    if (!q) return null;
    const keys = Object.keys(effHistory);
    const pick = (k, alts) => {
      const h = effHistory[k];
      return Array.isArray(h) && h.length > 0 ? { key: k, entry: h[0], alts: alts || null } : null;
    };
    const exact = keys.find(k => normKey(k) === q);
    if (exact) return pick(exact);
    const aliasKey = Object.keys(MAT_ALIASES).find(a => normKey(a) === q);
    if (aliasKey) {
      const targets = MAT_ALIASES[aliasKey].filter(t => effHistory[t]);
      if (targets.length) return pick(targets[0], targets.length > 1 ? targets.slice(1) : null);
    }
    if (q.length < MATCH_MIN) return null;
    const hits = keys.filter(k => normKey(k).includes(q));
    return hits.length === 1 ? pick(hits[0]) : null;
  };
  const matEntry = v => { const m = matMatch(v); return m ? m.entry : null; };
  // Supply-house entries now carry the unit their price is quoted in. Carrying
  // it onto the row matters: a per-FOOT wire price landing on a row labelled
  // "ea" is how a $0.64/ft coil gets billed as one $0.64 item.
  const updateMatIn = (pred,idx,v) => {
    const e = matEntry(v);
    if (e && Number(e.price) > 0) {
      const pct = Number(markup?.Materials) || 20;
      // Stored catalog prices are the supplier's PRINTED figure - pre-tax.
      // Entries that already carry the real tax (scanned off a receipt, which
      // prints its own tax line) are flagged taxIncluded and left alone, so
      // nothing is ever taxed twice.
      const taxPct = e.taxIncluded === true ? 0 : (Number(markup?.matTaxRate) || 0);
      const cost = Number(e.price) * (1 + taxPct / 100);
      const billed = Math.round(cost * (1 + pct/100) * 100) / 100;
      if (billed > 0) {
        patchIn(pred,idx, e.unit && UNITS.includes(e.unit) ? {desc:v,rate:billed,unit:e.unit} : {desc:v,rate:billed});
        return;
      }
    }
    patchIn(pred,idx,{desc:v});
  };
  // Shows where a price came from, under the row, in the supplier's own terms.
  // This is the cheapest guard that exists against the unit-basis class of
  // error: "$0.6359/ft - from $635.94 per M (CED) - 250 ft coil = $158.98" is
  // something you can check at a glance, where a bare $0.76 is not.
  // Returns null for hand-typed items and for the built-in defaults.
  const matNote = v => {
    const m = matMatch(v);
    const e = m ? m.entry : null;
    if (!e || !e.srcLabel) return null;
    const bits = [];
    // Only when it resolved to something other than what was typed - otherwise
    // the name is already sitting in the field right above.
    if (normKey(m.key) !== normKey(v)) bits.push(m.key);
    bits.push('$' + Number(e.price).toFixed(4) + '/' + (e.unit || 'ea'));
    bits.push('from $' + Number(e.srcPrice).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}) +
      ' per ' + e.srcLabel + (e.srcBasisQty > 1 ? ' (' + e.srcBasisQty + ')' : ''));
    if (e.pkgQty) bits.push(e.pkgLabel + ' = $' + (Number(e.price) * e.pkgQty).toFixed(2));
    bits.push(e.supplier + (e.date ? ' ' + e.date : ''));
    return { text: bits.join('  ·  '), review: e.review || null };
  };

  const scanReceipt = async (file) => {
    setScanning(true); setScanError('');
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ev => resolve(ev.target.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const prompt = `Extract information from this receipt for an invoice line item. Return ONLY JSON: {"description":"brief item description","amount":total_as_number}`;
      const resp = await fetch("/api/ai/messages", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+getToken()},
        body:JSON.stringify({
          model:"claude-haiku-4-5-20251001", max_tokens:500,
          context:"receipt_scan_invoice_modal",
          messages:[{role:"user",content:[
            {type:"image",source:{type:"base64",media_type:file.type||"image/jpeg",data:b64}},
            {type:"text",text:prompt}
          ]}]
        })
      });
      const rd = await resp.json();
      if (rd.error) throw new Error(rd.error.message || "AI error");
      const parsed = JSON.parse((rd.content||[]).map(c=>c.text||"").join("").replace(/```json|```/g,"").trim());
      const pct = markup?.Materials || 20;
      const billed = Math.round((parsed.amount||0) * (1+pct/100) * 100) / 100;
      syncItems(laborItems, [...matItems,{type:'material',desc:parsed.description||"Materials",qty:1,unit:"ea",rate:billed}]);
    } catch(e) {
      setScanError("Couldn't read receipt: "+e.message);
    }
    setScanning(false);
  };

  const onReceiptFileChange = e => { const f=e.target.files?.[0]; if(f) scanReceipt(f); e.target.value=''; };

  const saveNC = async () => {
    if(!ncData.name.trim()) return;
    setNcSaving(true);
    try { const c = await apiSaveClient(ncData); set("clientId",c.id); setNcModal(false); setNcData({name:"",email:"",phone:"",address:""}); }
    catch(e){ alert("Failed: "+e.message); }
    setNcSaving(false);
  };

  const SecHdr = ({label, color}) => (
    <div style={{display:'flex',alignItems:'center',margin:'14px 0 6px',borderBottom:`1px solid ${D.border}`,paddingBottom:4}}>
      <span style={{fontWeight:600,fontSize:13,color:color||D.text}}>{label}</span>
    </div>
  );
  const ColHdr = ({cols}) => (
    <div style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 32px",gap:6,marginBottom:4}}>
      {cols.map(h=><div key={h} style={{fontSize:11,color:D.muted}}>{h}</div>)}
    </div>
  );
  const XBtn = ({onClick}) => (
    <button style={{background:D.danger,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontSize:14,padding:"0 8px"}} onClick={onClick}>{'\u2715'}</button>
  );

  return (
    <Overlay>
      <div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(660px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
        <MHdr title={d.id?"Edit Invoice":"New Invoice"} onClose={onClose}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:14}}>
          <Field label="Invoice #"><input style={st.input} value={d.number||""} onChange={e=>set("number",e.target.value)}/></Field>
          <Field label="Date"><input style={st.input} type="date" value={d.date||""} onChange={e=>set("date",e.target.value)}/></Field>
          <Field label="Due Date"><input style={st.input} type="date" value={d.dueDate||""} onChange={e=>set("dueDate",e.target.value)}/></Field>
          {companyInfo?.lateFeeEnabled && (
            <div style={{gridColumn:"1/-1",background:D.surface,borderRadius:8,padding:"10px 14px",border:`1px solid ${d.lateFeeWaived?D.accent:D.border}`}}>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={!!d.lateFeeWaived} style={{width:16,height:16,accentColor:D.accent}} onChange={e=>set("lateFeeWaived",e.target.checked?1:0)}/>
                <span style={{fontSize:13,fontWeight:500,color:D.text}}>Exempt this invoice from late fees</span>
              </label>
              <div style={{fontSize:11,color:D.muted,marginTop:6,lineHeight:1.5}}>Late fees are switched on for your company. Tick this to stop the 1.5%/month fee being added to this invoice only.</div>
            </div>
          )}
          <div style={{gridColumn:"1/-1"}}>
            <Field label="Client">
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <select style={{...st.input,flex:1}} value={d.clientId||""} onChange={e=>set("clientId",parseInt(e.target.value))}>
                  {toArr(clients).length===0&&<option value="">— No clients yet —</option>}
                  {toArr(clients).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {apiSaveClient&&<button style={{...st.btn(),whiteSpace:"nowrap",flexShrink:0,background:"#1e3a5f",border:"1px solid #2d5f9e",color:"#7eb8f7"}} onClick={()=>setNcModal(true)}>+ New</button>}
              </div>
              {ncModal&&<Overlay zIndex={200}>
                <div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:14,padding:24,width:"100%",maxWidth:400}}>
                  <MHdr title="New Client" onClose={()=>setNcModal(false)}/>
                  {[["Name *","name","text","e.g. Johnson Residence"],["Email","email","email","client@email.com"],["Phone","phone","tel","(850) 555-0100"],["Address","address","text","123 Main St"]].map(([label,key,type,ph])=>(
                    <div key={key} style={{marginBottom:10}}>
                      <Field label={label}><input type={type} style={st.input} value={ncData[key]} placeholder={ph} onChange={e=>setNcData(x=>({...x,[key]:key==='phone'?formatPhone(e.target.value):e.target.value}))}/></Field>
                    </div>
                  ))}
                  <div style={{display:"flex",gap:8,marginTop:14}}>
                    <button style={{...st.btn("primary"),flex:1,marginLeft:0}} onClick={saveNC} disabled={ncSaving}>{ncSaving?"Saving...":"Save Client"}</button>
                    <button style={{...st.btn(),marginLeft:0}} onClick={()=>setNcModal(false)}>Cancel</button>
                  </div>
                </div>
              </Overlay>}
            </Field>
          </div>
          <div style={{gridColumn:"1/-1"}}>
            <Field label="Referred by (optional)">
              <select style={st.input} value={d.referredByClientId||""} onChange={e=>{
                const refId=e.target.value||null;
                const pct=Number(markup?.referralDiscountPct)||0;
                setD(x=>{
                  const base=toArr(x.items).filter(it=>!String(it.desc||'').startsWith('Referral Discount'));
                  if(!refId||pct===0) return{...x,referredByClientId:refId,items:base};
                  const sub=base.reduce((s,i)=>s+((Number(i.qty)||1)*(Number(i.rate)||0)),0);
                  const disc=Math.round(sub*pct/100*100)/100;
                  return{...x,referredByClientId:refId,items:[...base,{desc:`Referral Discount (${pct}%)`,qty:1,unit:'lot',rate:-disc}]};
                });
              }}>
                <option value="">— None —</option>
                {toArr(clients).filter(c=>String(c.id)!==String(d.clientId)).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Status">
            <select style={st.input} value={d.status} onChange={e=>set("status",e.target.value)}>{["Draft","Sent","Paid","Overdue"].map(v=><option key={v}>{v}</option>)}</select>
          </Field>
          <div style={{gridColumn:"1/-1"}}><Field label="Description"><textarea style={{...st.input,minHeight:60,resize:'vertical'}} value={d.notes||""} onChange={e=>set("notes",e.target.value)} placeholder="Invoice description or notes..."/></Field></div>
        </div>

        {/* Jobs. Each carries its own materials + labor, or a flat price. */}
        {matDatalist}
        {Object.keys(services).length>0&&<datalist id="svc-suggest">{Object.keys(services).map(n=><option key={n} value={n}/>)}</datalist>}
        {toArr(jobs).length>0&&<div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10,flexWrap:'wrap'}}>
          <select style={{...st.input,flex:1,maxWidth:220}} value={d.jobId||''} onChange={ev=>set('jobId',ev.target.value?Number(ev.target.value):null)}>
            <option value="">Link to Job...</option>
            {toArr(jobs).map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          {d.jobId&&<button style={{...st.btn(),marginLeft:0}} disabled={logImporting} onClick={importLog}>{logImporting?'Importing...':'Import Daily Log'}</button>}
        </div>}

        {jobGroups.map((g,gi)=>{
          const job=jobLine(g), mode=job.mode==='flat'?'flat':'tm';
          const isMat=it=>it.type==='material'&&gOf(it)===g;
          const isLab=it=>it.type!=='material'&&it.type!=='job'&&it.type!=='permit'&&gOf(it)===g;
          const mats=allItems.filter(isMat), labs=allItems.filter(isLab);
          // Everything the job carries, materials and labor together, in the one
          // order the Flat Rate view lists them. removeIn indexes by this pred,
          // so the pred and the rendered list must agree exactly or the X button
          // deletes a neighbour.
          const isIncl=it=>it.type!=='job'&&it.type!=='permit'&&gOf(it)===g;
          const incl=allItems.filter(isIncl);
          return (
            <div key={g} style={{border:'1px solid '+D.border,borderRadius:10,padding:'12px 14px',marginBottom:14}}>
              <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12,flexWrap:'wrap'}}>
                <span style={{fontSize:11,color:D.muted,whiteSpace:'nowrap'}}>JOB {gi+1}</span>
                <input style={{...st.input,flex:1,minWidth:170,fontWeight:600}} value={job.desc||''} list={mode==='flat'?'svc-suggest':undefined}
                  onChange={ev=>{
                    const v=ev.target.value;
                    if(mode==='flat'&&services[v]!==undefined) updateJobFields(g,{desc:v,rate:Number(services[v])||0});
                    else updateJob(g,'desc',v);
                  }} placeholder="What was done"/>
                {[['tm','Time & Material'],['flat','Flat Rate']].map(([v,lab])=>(
                  <button key={v} onClick={()=>setJobMode(g,v)} style={{padding:'4px 11px',borderRadius:20,border:'1px solid '+(mode===v?D.accent:D.border),background:mode===v?'rgba(249,115,22,0.12)':'transparent',color:mode===v?D.accent:D.muted,fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>{lab}</button>
                ))}
                {jobGroups.length>1&&<XBtn onClick={()=>removeJob(g)}/>}
              </div>
              {mode==='flat'?(
                <div>
                <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                  <span style={{fontSize:12,color:D.muted}}>Price</span>
                  <input style={{...st.input,maxWidth:150}} type="number" min="0" onFocus={ev=>ev.target.select()} value={job.rate||0} onChange={ev=>updateJob(g,'rate',ev.target.value)}/>
                  {Object.keys(services).length>0&&(
                    <select style={{...st.input,maxWidth:240}} value="" onChange={ev=>{const n=ev.target.value; if(!n)return; updateJobFields(g,{desc:n,rate:Number(services[n])||0}); ev.target.value='';}}>
                      <option value="">Use saved service...</option>
                      {Object.keys(services).sort().map(n=><option key={n} value={n}>{n+' - '+fmt(services[n])}</option>)}
                    </select>
                  )}
                </div>
                {/* The job's materials and labor survive the switch to Flat Rate
                    now, so they MUST be visible. Hiding them is exactly how this
                    went wrong the first time: lines nobody could see were still
                    being charged. Shown here they are the record of what the job
                    used, and anything still billing on top of the flat price is
                    called out in amber rather than silently zeroed behind the
                    contractor's back. */}
                {incl.length>0&&(()=>{
                  const billing=incl.filter(it=>(Number(it.qty)||0)*(Number(it.rate)||0)!==0);
                  const billSum=Math.round(billing.reduce((t,it)=>t+(Number(it.qty)||0)*(Number(it.rate)||0),0)*100)/100;
                  return (
                  <div style={{marginTop:12,borderTop:'1px dashed '+D.border,paddingTop:10}}>
                    <div style={{fontSize:11,color:D.muted,marginBottom:6}}>Included in this price — not billed separately</div>
                    {incl.map((item,i)=>{
                      const amt=(Number(item.qty)||0)*(Number(item.rate)||0);
                      const cst=Number(item.cost)>0?Number(item.cost)*(Number(item.qty)||0):0;
                      return (
                      <div key={i} style={{display:'grid',gridTemplateColumns:'3fr 1fr 1fr 2fr 32px',gap:6,alignItems:'center',fontSize:12,marginBottom:4}}>
                        <span style={{color:D.text,overflow:'hidden',textOverflow:'ellipsis'}}>{String(item.desc||'').trim()||'—'}</span>
                        <span style={{color:D.muted}}>{item.qty}</span>
                        <span style={{color:D.muted}}>{item.unit||''}</span>
                        <span style={{textAlign:'right',color:amt!==0?D.warning:D.muted,fontWeight:amt!==0?600:400}}>{amt!==0?'billing '+fmt(amt):(cst>0?'cost '+fmt(cst):'')}</span>
                        <XBtn onClick={()=>removeIn(isIncl,i)}/>
                      </div>);
                    })}
                    {billing.length>0&&(
                      <div style={{marginTop:8,fontSize:11.5,lineHeight:1.5,color:D.warning,background:D.warning+"18",border:"1px solid "+D.warning+"55",borderRadius:6,padding:"6px 9px"}}>
                        <b>{billing.length} line{billing.length===1?'':'s'} {billing.length===1?'is':'are'} billing {fmt(billSum)} on top of the flat price.</b> That is how a flat job stops being flat.
                        <button style={{...st.btn(),marginLeft:0,marginTop:6,display:'block'}} onClick={()=>setItems(allItems.map(it=>{
                          if(it.type==='job'&&gOf(it)===g) return {...it,rate:Math.round(((Number(it.rate)||0)+billSum)*100)/100};
                          if(isIncl(it)&&(Number(it.qty)||0)*(Number(it.rate)||0)!==0) return {...it,billedRate:it.billedRate!==undefined?it.billedRate:(Number(it.rate)||0),rate:0};
                          return it;
                        }))}>Fold {fmt(billSum)} into the flat price</button>
                      </div>
                    )}
                  </div>);
                })()}
                </div>
              ):(
                <div>
                  <SecHdr label="Materials" color="#4ade80"/>
                  {mats.length>0&&<ColHdr cols={["Material","Qty","Unit","Rate ($)",""]}/>}
                  {mats.map((item,i)=>(
                    <div key={i} style={{marginBottom:6}}>
                    <div style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 32px",gap:6}}>
                      <input style={st.input} value={item.desc||""} list={suggestId} onChange={ev=>updateMatIn(isMat,i,ev.target.value)} placeholder="Material or supply"/>
                      <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.qty} onChange={ev=>updateIn(isMat,i,"qty",ev.target.value)}/>
                      <select style={st.input} value={item.unit||"ea"} onChange={ev=>updateIn(isMat,i,"unit",ev.target.value)}>{UNITS.map(u=><option key={u}>{u}</option>)}</select>
                      <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.rate} onChange={ev=>updateIn(isMat,i,"rate",ev.target.value)}/>
                      <XBtn onClick={()=>removeIn(isMat,i)}/>
                    </div>
                    {(()=>{const n=matNote(item.desc); if(!n) return null; return n.review
                      ? <div style={{fontSize:11,lineHeight:1.45,color:D.warning,background:D.warning+"18",border:"1px solid "+D.warning+"55",borderRadius:6,padding:"5px 8px",marginTop:4}}><b>Check this price.</b> {n.review}<div style={{color:D.muted,marginTop:3}}>{n.text}</div></div>
                      : <div style={{fontSize:10.5,color:D.muted,padding:"2px 2px 0"}}>{n.text}</div>;})()}
                    </div>
                  ))}
                  <div style={{display:'flex',gap:8,marginTop:4,marginBottom:10,flexWrap:'wrap'}}>
                    <button style={{...st.btn(),marginLeft:0}} onClick={()=>addRow(g,'material',{unit:'ea'})}>+ Add Material</button>
                    {linkedUnbilled.length>0&&<button style={{...st.btn("success"),marginLeft:0}} onClick={()=>pullLinked(g)}>
                      + {linkedUnbilled.length} linked material{linkedUnbilled.length===1?"":"s"} ({fmt(linkedUnbilled.reduce((s,e)=>s+(Number(e.amount)||0),0))})
                    </button>}
                    {costedMats.length>0&&<span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:12,color:D.muted}}>
                      Markup
                      <input style={{...st.input,width:62,padding:"2px 6px"}} type="number" min="0" onFocus={ev=>ev.target.select()}
                        value={effMarkup} onChange={ev=>applyMatMarkup(parseFloat(ev.target.value)||0)}/>%
                    </span>}
                    <button style={{...st.btn(),marginLeft:0}} onClick={()=>{setScanError('');receiptFileRef.current?.click();}} disabled={scanning}>{scanning?"Scanning...":"Scan Receipt"}</button>
                    {scanError&&<span style={{color:D.danger,fontSize:12}}>{scanError}</span>}
                  </div>
                  <SecHdr label="Labor" color="#7eb8f7"/>
                  {labs.length>0&&<ColHdr cols={["Role","Qty","Unit","Rate ($)",""]}/>}
                  {labs.map((item,i)=>(
                    <div key={i} style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 32px",gap:6,marginBottom:6}}>
                      <select style={st.input} value={item.desc||""} onChange={ev=>setLaborRole(isLab,i,ev.target.value)}>{!isRole(item.desc||"")&&<option value={item.desc||""}>{item.desc||"Select role"}</option>}{LABOR_OPTS.map(o=><option key={o.desc} value={o.desc}>{o.desc}</option>)}</select>
                      <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.qty} onChange={ev=>updateIn(isLab,i,"qty",ev.target.value)}/>
                      <select style={st.input} value={item.unit||"hr"} onChange={ev=>updateIn(isLab,i,"unit",ev.target.value)}>{UNITS.map(u=><option key={u}>{u}</option>)}</select>
                      <input style={st.input} type="number" min="0" onFocus={ev=>ev.target.select()} value={item.rate} onChange={ev=>updateIn(isLab,i,"rate",ev.target.value)}/>
                      <XBtn onClick={()=>removeIn(isLab,i)}/>
                    </div>
                  ))}
                  <div style={{display:'flex',gap:8,marginTop:4,flexWrap:'wrap'}}>
                    <button style={{...st.btn(),marginLeft:0}} onClick={()=>addLaborRow(g,LABOR_OPTS[0].desc,LABOR_OPTS[0].rate)}>+ Add Labor</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <button style={{...st.btn(),marginLeft:0,marginBottom:12}} onClick={addJob}>+ Add Job</button>
        <input ref={receiptFileRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={onReceiptFileChange}/>


        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:16,borderTop:`1px solid ${D.border}`,paddingTop:12}}>
          <div>
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:D.muted,marginBottom:6}}>
              <input type="checkbox" checked={Boolean(permitLine)} onChange={ev=>togglePermit(ev.target.checked)}/>
              Permit fee
              {permitLine&&<input style={{...st.input,width:90,marginLeft:4,padding:"2px 6px"}} type="number" min="0" onFocus={ev=>ev.target.select()} value={permitLine.rate} onChange={ev=>setPermitRate(ev.target.value)}/>}
            </label>
          <div style={{color:D.success,fontWeight:500,fontSize:12}}>Subtotal: {fmt(calcSubtotal(d.items))}{getTaxRate()>0?` | Tax: ${fmt(calcTax(calcSubtotal(d.items)))}`:""} | Total: {fmt(calcTotal(d.items))}</div>
          </div>
          <div><button style={st.btn()} onClick={onClose}>Cancel</button><button style={st.btn("primary")} onClick={()=>{
            // any non-hourly priced line becomes a remembered service
            const learned={...services}; let changed=false;
            allItems.filter(it=>it.type==='job'&&it.mode==='flat').forEach(it=>{
              const n=String(it.desc||'').trim(), rt=Number(it.rate)||0;
              if(n&&rt>0&&learned[n]!==rt){learned[n]=rt;changed=true;}
            });
            if(changed){ setServices(learned); apiPut('/data/services',{services:learned}).catch(()=>{}); }
            onSave(d);
          }}>Save</button></div>
        </div>
      </div>
    </Overlay>
  );
}
